import * as t from 'io-ts';
import {isEmpty} from 'lodash';

import {getRenewalDate, isCustomPricingExpiring} from '../../../helpers/models/billingHelpers';
import {makeApiType} from '../apiTypes';
import {CompanyStatusesEnum} from '../companyTypes';
import {makeSafeArrayType} from '../types/arrayTypes';
import {collectEnumValues, makeEnumTypeGuard, makeSafeEnumRuntimeType} from '../types/enumTypes';
import {makeOptional} from '../types/optionalType';

/*
 * Constants.
 */

export enum AddonScopesEnum {
  /** addon is a number input and totaling addon price * number selected. */
  UNITS = 'units',
  /**
   * addon is a toggle and priced at $0 when turned on.
   * Billed on a monthly cadence totaling addon price * usage count.
   * e.g. ai answers
   */
  USAGE = 'usage',
  /**
   * company and company-fullonly have the same behavior
   * addon is a toggle and totaling addon price * seats when turned on.
   * e.g. shifts, smart qa
   */
  COMPANY = 'company',
  COMPANY_FULLONLY = 'company-fullonly',
}

export enum BillingRecurrencesEnum {
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
  QUARTERLY = 'quarterly',
  BIANNUALLY = 'biannually',
}

const isBillingRecurrence = makeEnumTypeGuard(BillingRecurrencesEnum);
const recurrenceList = collectEnumValues(BillingRecurrencesEnum);

export const RecurrenceMonthCount = {
  monthly: 1,
  quarterly: 3,
  biannually: 6,
  yearly: 12,
};

const RBillingAddonScopesEnum = makeSafeEnumRuntimeType(AddonScopesEnum, AddonScopesEnum.UNITS);

export const maxNumberOfInvoices = 100;

export enum PremiumAddonIdsEnum {
  WHATSAPP_USAGE = 'channel_premium_whatsapp_usage',
  AI_ANSWERS = 'ai_answers',
  WORKFORCE_MANAGEMENT = 'workforce_management',
  AI_SMART_QA = 'ai_smart_qa',
  AI_SMART_CSAT = 'ai_smart_csat',
  AI_SMART_QA_SUBSET = 'ai_smart_qa_subset',
  AI_SMART_CSAT_SUBSET = 'ai_smart_csat_subset',
  AI_COPILOT = 'ai_copilot',
  AI_AUTOPILOT = 'ai_autopilot',
}

export const whatsappAddonId = PremiumAddonIdsEnum.WHATSAPP_USAGE;

export const frontHostingAddonId = 'hosting_support_services';

/** Used for display addon only. Edit is always disabled */
export const internalAddonIds = [frontHostingAddonId];

export const starterPlanType = 'starter';

/** Used as an exception for recommending upgrade to T3.
 * All other legacy plan types (plus, pro, prime,premium, etc) are recommended to upgrade to T2 */
export const legacyEnterprisePlanType = 'enterprise';

export const downsellThreshold = 0.7;

export const uncoveredArrMax = 25_000;

export const selfServeRecurrences = [BillingRecurrencesEnum.MONTHLY, BillingRecurrencesEnum.YEARLY];

export const defaultRecurrence = BillingRecurrencesEnum.YEARLY;
export const defaultPlanMax = 10_000;
export const monthlyMaxSeats = 35;
export const defaultPlanMin = 2;

/*
 * Runtime types.
 */

const RBillingRecurrencesEnum = makeSafeEnumRuntimeType(BillingRecurrencesEnum, defaultRecurrence);

const RBillingCardAddress = t.type({
  name: t.union([t.string, t.null]),
  addressLine1: t.union([t.string, t.null]),
  addressLine2: t.union([t.string, t.null]),
  addressCity: t.union([t.string, t.null]),
  addressState: t.union([t.string, t.null]),
  addressCountry: t.union([t.string, t.null]),
  addressZip: t.union([t.string, t.null]),
});

const RBillingCard = t.readonly(
  t.intersection([
    RBillingCardAddress,
    t.type({
      brand: t.union([t.string, t.null]),
      expMonth: t.union([t.number, t.null]),
      expYear: t.union([t.number, t.null]),
      last4: t.union([t.string, t.null]),
    }),
  ]),
);

const RBillingBankModel = t.readonly(
  t.type({
    id: t.string,
    bankName: t.string,
    last4: t.string,
  }),
);

const RBillingSubscriptionModel = t.intersection([
  t.type({
    cancelAtPeriodEnd: makeOptional(t.boolean),
    endsAt: makeOptional(t.number),
  }),
  t.partial({
    // you can have a customer with no active subscription
    id: t.string,
  }),
]);

const RBillingInvoiceAddress = t.type({
  city: t.union([t.string, t.null]),
  country: t.union([t.string, t.null]),
  line1: t.union([t.string, t.null]),
  line2: t.union([t.string, t.null]),
  name: t.union([t.string, t.null]),
  postalCode: t.union([t.string, t.null]),
  state: t.union([t.string, t.null]),
});

const RBillingTaxInfo = t.type({
  taxId: t.union([t.string, t.null]),
  taxType: t.union([t.string, t.null]),
});

const RPlaidSettings = t.type({
  enabled: t.boolean,
});

const RPlaidMetaDataAccount = t.type({
  id: t.string,
  mask: t.string,
});

const RPlaidMetaDataInstitution = t.type({
  name: t.string,
});

const RPlaidMetadata = t.readonly(
  t.type({
    accounts: t.array(RPlaidMetaDataAccount),
    institution: t.union([RPlaidMetaDataInstitution, t.null]),
  }),
);

const RBillingCoupon = t.type({
  id: t.string,
  percentOff: t.union([t.number, t.null]),
  amountOff: t.union([t.number, t.null]),
  valid: t.boolean,
  productIds: makeOptional(t.array(t.string)),
});

const RPlaidConfiguration = t.type({
  publicToken: t.string,
  valid: t.boolean,
  metadata: RPlaidMetadata,
});

const RBillingPlanLimits = t.intersection([
  t.type({
    type: t.string,
  }),
  t.partial({
    subType: t.string,
    current: t.number,
    max: t.number,
    extra: t.array(t.string),
  }),
]);

const RBillingSuggestedRepliesLimits = t.type({
  used: t.number,
  available: t.number,
  isTrial: makeOptional(t.boolean),
  trialEndDate: makeOptional(t.number),
  isTrialEnded: makeOptional(t.boolean),
});

const RBillingProductsLimits = t.record(t.string, t.array(RBillingPlanLimits));
const RBillingAddonUtilization = t.type({
  available: t.number,
  consumed: t.number,
  isTrial: makeOptional(t.boolean),
  trialEndDate: makeOptional(t.number),
  isTrialEnded: makeOptional(t.boolean),
  enforceUsageSeats: makeOptional(t.boolean),
});
const RBillingUsage = t.partial({
  numFullTeammates: t.number,
  numAllTypeTeammatesIncludingDeleted: t.number,
  numSharedChannels: t.number,
  numChannels: t.number,
  channelsPerTeammateRatio: makeOptional(t.number),
  limits: t.union([RBillingProductsLimits, t.null]),
  aiAnswers: t.union([RBillingAddonUtilization, t.null]),
  aiAutopilot: t.union([RBillingAddonUtilization, t.null]),
  workforceManagement: t.union([RBillingAddonUtilization, t.null]),
  aiSuggestedReplies: RBillingSuggestedRepliesLimits,
});

const RBillingTier = t.type({
  amount: t.number,
  upTo: t.union([t.number, t.literal('inf')]),
});

const RBillingConfig = t.partial({
  addons: t.unknown,
  tiers: t.union([t.array(RBillingTier), t.null]),
  expiration: makeOptional(t.number),
});

const RBillingPeriodPricing = t.type({
  teammatePrice: t.number,
  stripeAmount: t.number,
});

const RBillingPricing = t.partial({
  monthly: RBillingPeriodPricing,
  quarterly: RBillingPeriodPricing,
  biannually: RBillingPeriodPricing,
  yearly: RBillingPeriodPricing,
  usagePrice: makeOptional(t.number),
});

const RBillingAddonBase = t.type({
  id: t.string,
  name: t.string,
  scope: RBillingAddonScopesEnum,
});

const RBillingPlanAddon = t.intersection([
  RBillingAddonBase,
  t.type({
    productId: t.string,
    pricing: t.union([RBillingPricing, t.null]),
    selfServed: t.boolean,
    unitMult: t.number,
    isRequired: t.boolean,
    maxSeats: makeOptional(t.number),
    minSeats: makeOptional(t.number),
    isAutoOptIn: t.boolean,
    accessType: makeOptional(t.string),
    circleId: makeOptional(t.number),
  }),
  t.partial({
    features: makeSafeArrayType(t.string),
  }),
]);

const RBillingIncludedAddon = t.type({
  id: t.string,
  quantity: t.number,
});

export const RBillingPlan = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    type: t.string,
    externalName: t.string,
    addons: t.record(t.string, RBillingPlanAddon),
    includedAddons: makeSafeArrayType(RBillingIncludedAddon),
    isTrial: t.boolean,
    isManagedTrial: t.boolean,
    minSeats: t.number,
    maxSeats: t.number,
    selfServed: t.boolean,
    isPromoted: t.boolean,
    mostPopular: t.boolean,
    canDowngradeTo: t.boolean,
    canDowngradeFrom: t.boolean,
    approachingMaxSeatThreshold: t.number,
  }),
  t.partial({
    // some plans like trial won't have this property
    pricing: t.union([RBillingPricing, t.null]),
    features: t.array(t.string),
  }),
]);

const RBillingDetails = t.type({
  plans: t.array(RBillingPlan),
  plan: t.union([RBillingPlan, t.null]),
  recurrence: makeOptional(RBillingRecurrencesEnum),
  isCancelled: t.boolean,
  endDate: makeOptional(t.number),
  amCovered: t.boolean,
  commitmentTerm: makeOptional(t.string),
});

const RBillingLicensesAddon = t.type({
  seats: t.number,
  fixedSeats: t.number,
  product: RBillingAddonBase,
});

export const RBillingLicenses = t.partial({
  seats: t.number,
  config: RBillingConfig,
  addons: t.record(t.string, RBillingLicensesAddon),
  product: t.type({id: t.string}),
  recurrence: RBillingRecurrencesEnum,
  isFromMigrationScript: makeOptional(t.boolean),
});

const RBillingScheduledLicenses = t.intersection([
  RBillingLicenses,
  t.type({
    date: t.number,
  }),
]);

const RBillingAccountManager = t.type({
  name: t.string,
  email: t.string,
});

// return value of parseCustomer
const RBilling = t.readonly(
  t.type({
    billing: t.type({
      hasPaymentMethod: t.boolean,
      subscription: t.union([RBillingSubscriptionModel, t.null]),
      recurrence: RBillingRecurrencesEnum,
      email: makeOptional(t.string),
      contacts: t.array(t.string),
      taxInfo: t.union([RBillingTaxInfo, t.null]),
      coupon: t.union([RBillingCoupon, t.null]),
      invoiceAddress: makeOptional(RBillingInvoiceAddress),
      bank: makeOptional(RBillingBankModel),
      card: makeOptional(RBillingCard),
      stripeError: t.union([t.string, t.null]),
      isLastBillingCycle: t.boolean,
    }),
    plan: t.union([RBillingPlan, t.null]),
    pendingPlan: t.union([RBillingPlan, t.null]),
    scheduledPlan: t.union([RBillingPlan, t.null]),
    licenses: t.union([RBillingLicenses, t.null]),
    pendingLicenses: t.union([RBillingLicenses, t.null]),
    nextScheduledLicenses: t.union([RBillingScheduledLicenses, t.null]),
    plaid: RPlaidSettings,
    stripeApiKey: t.union([t.string, t.null]),
    paymentLink: t.string,
    isCancelled: t.boolean,
    isBlockedBilling: t.boolean,
    isBlocked: t.boolean,
    isActive: t.boolean,
    subscriptionEndsAt: makeOptional(t.number),
    hasCustomPlan: t.boolean,
    amCovered: t.boolean,
    accountManager: t.union([RBillingAccountManager, t.null]),
    renewalDate: t.union([t.number, t.null]),
    isMonthToMonth: t.boolean,
  }),
);

const RCancellationSurvey = t.type({
  answers: t.array(t.string),
  reason: t.union([t.string, t.null]),
});

export enum InvoiceStatusesEnum {
  DELETED = 'deleted',
  DRAFT = 'draft',
  OPEN = 'open',
  PAID = 'paid',
  UNCOLLECTIBLE = 'uncollectible',
  VOID = 'void',
}

const RBillingInvoiceStatusesEnum = makeSafeEnumRuntimeType(InvoiceStatusesEnum, InvoiceStatusesEnum.DELETED);

const RBillingInvoice = t.intersection([
  t.type({
    date: t.number,
    total: t.number,
    status: t.union([RBillingInvoiceStatusesEnum, t.null]),
    dueDate: t.union([t.number, t.null]),
  }),
  t.partial({
    id: t.union([t.string, t.null]),
    url: t.union([t.string, t.null]),
  }),
]);

export enum LicensesStatusesEnum {
  AVAILABLE = 'available',
  FILLED = 'filled',
}

const RLicensesStatusesEnum = makeSafeEnumRuntimeType(LicensesStatusesEnum, LicensesStatusesEnum.FILLED);

const RLicensesStatus = t.type({
  full: RLicensesStatusesEnum,
});

const RBillingPlaidLinkToken = makeOptional(t.string);

export interface BillingContactEmailModel {
  email: string;
  contacts: Array<string>;
}

/*
 * Types.
 */

export interface AddonModel {
  id: string;
  name: string;
  productId: string;
  pricing: PricingModel | null;
  scope: AddonScopesEnum;
  selfServed: boolean;
  unitMult: number;
  isRequired: boolean;
  isAutoOptIn: boolean;
  minSeats: number | undefined;
  maxSeats: number | undefined;
  accessType: string | undefined;
  circleId: number | undefined;
}

export interface AddonLicenseModel {
  product: {
    id: string;
    name: string;
    scope: AddonScopesEnum;
    seatPrice?: number | null;
  };
  seats: number;
  fixedSeats: number;
}

// type used as PUT /billing2 request body to update stripe / plaid token
export interface UpdatePaymentMethodInfoModel {
  token: string;
  plaidAccountId: string;
}

export interface UpdateBillingAddonPayloadModel {
  id: string;
  units: number;
}

// type used in PUT billing/current and billing/pending to update changes
export interface UpdateBillingPayloadModel {
  stripeIdempotencyKey: string;
  seats: number | null;
  plan: string | null;
  addons: Array<UpdateBillingAddonPayloadModel> | null;
  recurrence: BillingRecurrencesEnum | null;
  coupon: string | null;
  delayUpdate?: boolean;
}

// type used in PUT billing/new to create company subscription for the first time
export interface CreateNewBillingPayloadModel extends UpdateBillingPayloadModel {
  token?: string;
  plaidAccountId?: string;
}

// type used as PUT /update_sfdc_friction_alert request body to update SFDC fields
export interface UpdateFrictionAlertInfoModel {
  scenario: string;
  planId?: string;
  feature?: string;
  isInternal?: boolean;
}

export interface BillingBankModel extends t.TypeOf<typeof RBillingBankModel> {}
export interface BillingCardModel extends t.TypeOf<typeof RBillingCard> {}
export interface BillingCardAddressModel extends t.TypeOf<typeof RBillingCardAddress> {}
export interface BillingCouponModel extends t.TypeOf<typeof RBillingCoupon> {}
export interface BillingPricingModel extends t.TypeOf<typeof RBillingPricing> {}
export interface BillingSubscriptionModel extends t.TypeOf<typeof RBillingSubscriptionModel> {}
export interface BillingModel extends t.TypeOf<typeof RBilling> {}
export interface BillingInvoiceAddressModel extends t.TypeOf<typeof RBillingInvoiceAddress> {}
export interface BillingPlanModel extends t.TypeOf<typeof RBillingPlan> {}
export interface BillingTaxInfoModel extends t.TypeOf<typeof RBillingTaxInfo> {}
export interface PlaidSettingsModel extends t.TypeOf<typeof RPlaidSettings> {}
export interface PlaidMetaModel extends t.TypeOf<typeof RPlaidMetadata> {}
export interface PlaidConfigurationModel extends t.TypeOf<typeof RPlaidConfiguration> {}
export interface InvoiceModel extends t.TypeOf<typeof RBillingInvoice> {}
export interface TierPriceModel extends t.TypeOf<typeof RBillingTier> {}
export interface PlanModel extends t.TypeOf<typeof RBillingPlan> {}
export interface BillingPlanLimitsModel extends t.TypeOf<typeof RBillingPlanLimits> {}
export interface BillingProductsLimitsModel extends t.TypeOf<typeof RBillingProductsLimits> {}
export interface UsageModel extends t.TypeOf<typeof RBillingUsage> {}
export interface LicensesModel extends t.TypeOf<typeof RBillingLicenses> {}
export interface ScheduledLicensesModel extends t.TypeOf<typeof RBillingScheduledLicenses> {}
export interface PeriodPricingModel extends t.TypeOf<typeof RBillingPeriodPricing> {}
export interface PricingModel extends t.TypeOf<typeof RBillingPricing> {}
export interface BillingTierModel extends t.TypeOf<typeof RBillingTier> {}
export interface BillingAccountManagerModel extends t.TypeOf<typeof RBillingAccountManager> {}
export const RInvoices = t.array(RBillingInvoice);
export interface CancellationSurveyModel extends t.TypeOf<typeof RCancellationSurvey> {}
export interface LicensesStatusModel extends t.TypeOf<typeof RLicensesStatus> {}
export interface BillingDetails extends t.TypeOf<typeof RBillingDetails> {}
export interface BillingAddonUtilization extends t.TypeOf<typeof RBillingAddonUtilization> {}
export interface BillingSuggestedRepliesLimits extends t.TypeOf<typeof RBillingSuggestedRepliesLimits> {}

/*
 * IO methods.
 */

// Used when parsing coupon from `getBilling`, which can be null
const parseSrcCoupon = (coupon: any): BillingCouponModel | null => {
  if (!coupon) {
    return null;
  }
  return parseCoupon(coupon);
};

// Used to parse a coupon you're sure to have. like when we call `getCoupon`
const parseCoupon = (coupon: any): BillingCouponModel => ({
  id: coupon.id,
  amountOff: coupon.amount_off,
  percentOff: coupon.percent_off,
  valid: Boolean(coupon.valid),
  productIds: coupon.product_ids,
});

const parseSrcPricing = (srcPricing: any) => {
  if (!srcPricing) {
    return null;
  }
  const pricing: PricingModel = {};

  // Use usagePrice for usage based addons. Recurrence price are set as 0 for display.
  if (Number.isFinite(srcPricing.usagePrice)) {
    pricing.usagePrice = srcPricing.usagePrice;
  }

  Object.keys(srcPricing).forEach((r) => {
    const recurrence = r && isBillingRecurrence(r) ? r : null;
    if (!recurrence) {
      return;
    }

    /** Only map valid recurrence pricing object since keys such as usagePrice is a number */
    const recurrencePricing = recurrenceList.includes(recurrence) && srcPricing[recurrence];
    /**
     * Some standalone plans have quarterly or biannually recurrence as null.
     * Addon may have monthly pricing disabled.
     * Iterations 10 or under do not have stripe_amount.
     */
    if (recurrencePricing && !recurrencePricing.disabled) {
      pricing[recurrence] = {
        teammatePrice: recurrencePricing.teammate_price,
        stripeAmount: recurrencePricing.stripe_amount || 1,
      };
    }
  });
  return pricing;
};

const parseSrcPlanAddons = (srcAddons: any, autoOptInAddons: Array<string>) => {
  const addons = {} as Record<string, AddonModel>;
  Object.keys(srcAddons)
    .sort()
    .forEach((addonId) => {
      const srcAddon = srcAddons[addonId];
      addons[addonId] = {
        ...srcAddon,
        selfServed: Boolean(srcAddon.self_served),
        unitMult: srcAddon.unit_mult || 1,
        isRequired: Boolean(srcAddon.limits?.is_required),
        isAutoOptIn: autoOptInAddons.includes(addonId),
        minSeats: srcAddon.limits?.min_seats,
        maxSeats: srcAddon.limits?.max_seats,
        pricing: parseSrcPricing(srcAddon.pricing),
        features: srcAddon.features,
        productId: srcAddon.product_id,
        accessType: srcAddon.access_type,
        circleId: srcAddon.circle_id,
      };
    });
  return addons;
};

const parseSrcPlan = (srcPlan: any, autoOptInAddons: Array<string>): PlanModel | null => {
  if (!srcPlan) {
    return null;
  }

  const planLimits = srcPlan.limits;

  return {
    id: srcPlan.id,
    name: srcPlan.name,
    type: srcPlan.type,
    externalName: srcPlan.external_name ?? '',
    isTrial: srcPlan.is_trial,
    isManagedTrial: srcPlan.is_managed_trial,
    addons: parseSrcPlanAddons(srcPlan.addons, autoOptInAddons),
    includedAddons: srcPlan.included_addons || [],
    pricing: parseSrcPricing(srcPlan.pricing),
    features: srcPlan.features,

    minSeats: planLimits?.min_seats ?? defaultPlanMin,
    maxSeats: planLimits?.max_num_teammates ?? defaultPlanMax,
    approachingMaxSeatThreshold: planLimits?.approaching_max_seat_threshold || defaultPlanMax,

    selfServed: Boolean(planLimits?.self_served),
    isPromoted: Boolean(planLimits?.is_promoted),
    mostPopular: Boolean(planLimits?.most_popular),
    canDowngradeTo: Boolean(planLimits?.can_downgrade_to),
    canDowngradeFrom: Boolean(planLimits?.can_downgrade_from),
  };
};

const parseLimits = (srcLimits: any) => {
  // we do not send over limits in all plans json, but share one parser.
  // Some limits entries can be undefined.
  if (!srcLimits) {
    return null;
  }

  const allExceedingLimits: Record<string, any> = {};
  Object.keys(srcLimits).forEach((planOrAddonId: any) => {
    const item = srcLimits[planOrAddonId];
    if (!item) {
      return;
    }
    const planLimits = parsePlanLimits(item?.exceeding);
    if (!planLimits) {
      return;
    }
    allExceedingLimits[planOrAddonId] = planLimits;
  });

  return allExceedingLimits;
};

const parseAddonUsage = (srcUsage: any) => {
  if (!srcUsage) {
    return null;
  }

  return {
    available: srcUsage.available || 0,
    consumed: srcUsage.consumed || 0,
    isTrial: Boolean(srcUsage.is_trial),
    isTrialEnded: Boolean(srcUsage.is_trial_ended),
    trialEndDate: srcUsage.trial_end_date,
    enforceUsageSeats: Boolean(srcUsage.enforce_usage_seats),
  };
};

const parseAiSuggestedRepliesUsage = (srcUsage: any): BillingSuggestedRepliesLimits => {
  if (!srcUsage) {
    return {used: 0, available: 0, isTrial: undefined, trialEndDate: undefined, isTrialEnded: undefined};
  }

  return {
    used: srcUsage.used || 0,
    available: srcUsage.available || 0,
    isTrial: srcUsage.is_trial,
    trialEndDate: srcUsage.trial_end_date,
    isTrialEnded: srcUsage.is_trial_ended,
  };
};

const parsePlanLimits = (srcLimits: any) => {
  // we do not send over limits in all plans json, but share one parser.
  // Some limits entries can be undefined.
  if (!srcLimits) {
    return null;
  }

  return srcLimits.map((limit: any) => ({
    type: limit.type,
    subType: limit.subType,
    current: limit.current,
    max: limit.max,
    extra: limit.extra,
  }));
};

export const parseBillingLicenses = (srcLicenses: any): LicensesModel | null => {
  if (isEmpty(srcLicenses)) {
    return null;
  }

  const srcTiers = srcLicenses.config?.tiers;
  const tiers = srcTiers ? srcTiers.map((tier: any) => ({amount: tier.amount, upTo: tier.up_to})) : null;
  const addons = srcLicenses.config?.addons;

  return {
    ...srcLicenses,
    config: {
      tiers,
      addons,
    },
  };
};

export const parseBillingScheduledLicenses = (srcLicenses: any): ScheduledLicensesModel | null => {
  const licenses = parseBillingLicenses(srcLicenses) as ScheduledLicensesModel;
  if (licenses) {
    licenses.date = srcLicenses.date;
  }
  return licenses;
};

const parseSrcTaxInfo = (srcTaxInfo: any) => {
  if (!srcTaxInfo) {
    return null;
  }
  return {
    taxId: srcTaxInfo.taxId ?? null,
    taxType: srcTaxInfo.taxType,
  };
};

export function parseBilling(src: any): BillingModel {
  const cardBlob = src.billing?.card;
  // Temporary fix for expired credit card:
  // When receiving cardBlob in unexpected format, we consider the card expired.
  // Do not populate CC input fields so that user could update the entire card.
  const card = cardBlob
    ? {
        addressLine1: cardBlob.address_line1 || null,
        addressLine2: cardBlob.address_line2 || null,
        addressCity: cardBlob.address_city || null,
        addressState: cardBlob.address_state || null,
        addressCountry: cardBlob.address_country || null,
        addressZip: cardBlob.address_zip || null,
        brand: cardBlob.brand || null,
        expMonth: cardBlob.exp_month || null,
        expYear: cardBlob.exp_year || null,
        last4: cardBlob.last4 || null,
        name: cardBlob.name || null,
      }
    : undefined;

  const bankBlob = src.billing?.bank;
  const bank = bankBlob
    ? {
        id: bankBlob.id,
        bankName: bankBlob.bank_name,
        last4: bankBlob.last4,
      }
    : undefined;

  // src.billing.invoiceAddress can be null or undefined. Forcing to only one value `undefined`. Could be done on the BE too.
  const invoiceAddress = src.billing.invoiceAddress ? src.billing.invoiceAddress : undefined;

  const currentLicenses = src.licenses;
  const pendingLicenses = src.licenses.pending;

  const renewalDate = getRenewalDate(src.contract?.end_date, src.billing.subscription?.ends_at);
  const isMonthToMonth = !src.contract || src.contract.commitment_term === 'monthly';

  const hasExpiringCustomPricing = isCustomPricingExpiring(renewalDate, src.licenses);

  const subscriptionEndsAt = !hasExpiringCustomPricing ? src.billing.subscription?.ends_at : undefined;

  return {
    billing: {
      hasPaymentMethod: src.billing.hasPaymentMethod || false,
      subscription: src.billing.subscription
        ? {
            id: src.billing.subscription.id,
            cancelAtPeriodEnd: src.billing.subscription.cancel_at_period_end,
            endsAt: src.billing.subscription.ends_at,
          }
        : null,
      coupon: parseSrcCoupon(src.billing.coupon),
      invoiceAddress,
      recurrence: src.billing.recurrence,
      email: src.billing.email,
      contacts: src.billing.contacts || [],
      taxInfo: parseSrcTaxInfo(src.billing.taxInfo),
      card,
      bank,
      isLastBillingCycle: src.billing.last_billing_cycle || false,
      stripeError: src.billing.stripeError || null,
    },
    plan: parseSrcPlan(src.plan, []),
    pendingPlan: parseSrcPlan(src.pending_plan, []),
    scheduledPlan: parseSrcPlan(src.scheduled_plan, []),
    licenses: parseBillingLicenses(currentLicenses),
    pendingLicenses: parseBillingLicenses(pendingLicenses),
    nextScheduledLicenses: parseBillingScheduledLicenses(src.licenses?.scheduled?.[0]),
    plaid: {enabled: Boolean(src.plaid?.enabled)},
    stripeApiKey: src.stripe,
    paymentLink: src.paymentLink,
    isCancelled: Boolean(
      src.contract ? src.contract?.cancel_at_end : src.billing.subscription?.cancel_at_period_end,
    ),
    isBlockedBilling: src.billing.status === CompanyStatusesEnum.BLOCKED_BILLING,
    isBlocked: src.billing.status === CompanyStatusesEnum.BLOCKED,
    isActive: src.billing.status === CompanyStatusesEnum.OK,
    subscriptionEndsAt,
    hasCustomPlan: src.hasCustomPlan,
    amCovered: src.am_covered, // From SFDC Account_Segment_c === 'Sales Led'
    accountManager: src.am,
    renewalDate,
    isMonthToMonth,
  };
}

function parseUsage(src: any): UsageModel {
  return {
    numFullTeammates: src.num_full_teammates ?? 0,
    numAllTypeTeammatesIncludingDeleted: src.num_all_type_teammates_including_deleted ?? 0,
    numSharedChannels: src.num_shared_channels ?? 0,
    numChannels: src.num_channels ?? 0,
    channelsPerTeammateRatio: src.channels_per_teammate_ratio,
    limits: parseLimits(src.limits),
    aiAnswers: parseAddonUsage(src.aiAnswers),
    aiAutopilot: parseAddonUsage(src.aiAutopilot),
    workforceManagement: parseAddonUsage(src.workforceManagement),
    aiSuggestedReplies: parseAiSuggestedRepliesUsage(src.aiSuggestedReplies),
  };
}

function parseInvoicesApiToken(src: any): Array<InvoiceModel> {
  return src.map((invoice: any) => ({
    id: invoice.id ?? '',
    status: invoice.status,
    dueDate: invoice.due_date,
    date: invoice.date,
    url: invoice.url ?? '',
    total: invoice.total,
  }));
}

function parseLicensesStatus(src: any): LicensesStatusModel {
  return {
    full: src.full,
  };
}

function parseBillingDetails(src: any): BillingDetails {
  const srcPlans = src.plans || [];

  return {
    plans: srcPlans.map((p: any) => parseSrcPlan(p, src.auto_opt_in_addons || [])),
    plan: parseSrcPlan(src.plan, []),

    recurrence: src.recurrence,
    isCancelled: Boolean(src.is_cancelled),
    endDate: src.end_date,
    amCovered: Boolean(src.am_covered),
    commitmentTerm: src.commitment_term,
  };
}

function parseBillingPlaidLinkToken(src: any): string | undefined {
  return src.link_token;
}

/*
 * API types.
 */

export const ApiBillingModel = makeApiType('billing', RBilling, parseBilling);
export const ApiBillingUsage = makeApiType('billingUsage', RBillingUsage, parseUsage);
export const ApiInvoicesModel = makeApiType('billingInvoices', RInvoices, parseInvoicesApiToken);
export const ApiCouponModel = makeApiType('billingCoupon', RBillingCoupon, parseCoupon);
export const ApiLicensesStatusModel = makeApiType('licensesStatus', RLicensesStatus, parseLicensesStatus);
export const ApiBillingDetails = makeApiType('billingDetails', RBillingDetails, parseBillingDetails);
export const ApiPlaidLinkToken = makeApiType(
  'billingPlaidLinkToken',
  RBillingPlaidLinkToken,
  parseBillingPlaidLinkToken,
);

export type BillingUsageTeammates = {
  numFullTeammates: number;
  numAllTypeTeammatesIncludingDeleted: number;
};
